<template>
  <div class="w-full">
    <mia-is-allowed to="plan_car_dispatches">
      <h1>Fahrzeuge zur Auslieferung einplanen</h1>

      <div class="flex justify-between p-4 bg-gray-300 rounded-t-md">
        <div class="flex items-center flex-shrink-0">
          <mia-column-filter
            v-model="visibleColumns"
            :config="columnConfig"
            @reset="resetVisibleColumns"
          />

          <label
            for="filter-query"
            class="mb-0 ml-4 font-normal"
          >
            Suchen:
          </label>

          <input
            id="filter-query"
            v-model="filterQuery"
            type="text"
            placeholder="Tabelle durchsuchen…"
            class="ml-4 form-input"
          >

          <label class="inline-flex items-center flex-shrink-0 mb-0 ml-12 font-normal cursor-pointer">
            <mia-checkbox
              :value="1"
              :checked="includePlannedCars"
              class="w-5 h-5"
              @input="includePlannedCars = $event.target.checked"
            />
            <span class="ml-2">Bereits eingeplante Fahrzeuge anzeigen</span>
          </label>
        </div>

        <div class="flex items-center">
          <span>Zeige</span>

          <mia-select
            :value="perPage"
            class="mx-3"
            @input="onPerPageChanged"
          >
            <option :value="10">
              10
            </option>
            <option :value="25">
              25
            </option>
            <option :value="50">
              50
            </option>
            <option :value="100">
              100
            </option>
          </mia-select>

          <span>Einträge</span>
        </div>
      </div>

      <div
        v-if="$wait.is('loading dispatchable cars')"
        class="flex items-center justify-center p-12"
      >
        <mia-spinner class="w-12 h-12 text-orange-mia" />
      </div>
      <template v-else-if="renderDataTable">
        <datatable
          ref="datatable"
          name="dispatchable-cars-table"
          :columns="columns"
          :data="dispatchableCars"
          :filter="filterQuery"
          :per-page="perPage"
          class="sticky w-full table-auto datatable"
          v-on="{
            'vue-datatable::page-changed': onPageChanged,
            'vue-datatable::page-count-changed': onPageCountChanged,
          }"
          @check-all="onChecked"
          @uncheck-all="onUnchecked"
          @checked="onChecked"
          @unchecked="onUnchecked"
        >
          <template #no-results>
            <div class="flex items-center justify-center p-4">
              Derzeit sind keine Fahrzeuge verfügbar, die zur Auslieferung eingeplant werden können.
            </div>
          </template>
        </datatable>

        <div class="flex p-4 bg-gray-300 rounded-b-md">
          <div class="flex items-center w-3/12">
            <mia-datatable-results-counter
              :page="page"
              :per-page="perPage"
              :total="total"
              class="flex items-center"
            />
          </div>

          <div class="flex items-center justify-center w-6/12">
            <mia-datatable-pager
              v-model="page"
              table="dispatchable-cars-table"
              class="ml-8"
              type="abbreviated"
            />
          </div>

          <div class="flex items-center justify-end w-3/12">
            <button
              class="inline-flex items-center h-10 px-4 py-2 ml-4 text-gray-100 bg-blue-600 rounded focus:outline-none focus:shadow-outline"
              :class="{
                'hover:bg-blue-700': hasSelectedCars && !$wait.any,
                'opacity-50': !hasSelectedCars
              }"
              :disabled="!hasSelectedCars || $wait.any"
              type="button"
              @click="planSelectedCars"
            >
              Gewählte{{ selectedCars.length === 1 ? 's' : '' }} Fahrzeug{{ selectedCars.length === 1 ? '' : 'e' }} einplanen
            </button>
          </div>
        </div>
      </template>

      <mia-modal
        v-if="showPlanDispatchableCarsModal"
        id="plan-dispatchable-cars-modal"
        classes="v--modal flex flex-col"
        width="70%"
        height="auto"
        :show="showPlanDispatchableCarsModal"
        @closed="showPlanDispatchableCarsModal = false"
        @opened="onPlanDispachableCarsModalOpened"
      >
        <mia-form @submit="onSubmit">
          <mia-modal-header>
            <h3 class="py-0 my-0">
              Gewählte{{ selectedCars.length === 1 ? 's' : '' }} Fahrzeug{{ selectedCars.length === 1 ? '' : 'e' }} einplanen
            </h3>
          </mia-modal-header>

          <div
            ref="plannedDispatchContainerRef"
            class="p-4"
          >
            <div class="pb-4 mb-4 border-b border-gray-300">
              <div class="font-bold">
                {{ selectedCars.length === 1 ? 'Das folgende Fahrzeug wurde' : 'Die folgenden Fahrzeuge wurden' }} zur Einplanung ausgewählt:
              </div>
              <ul
                class="pr-2 mt-2 overflow-x-hidden overflow-y-auto divide-y divide-gray-300"
                style="max-height: 16rem;"
              >
                <li
                  v-for="selectedCar in selectedCars"
                  :key="selectedCar.id"
                  class="flex items-center justify-between py-2"
                >
                  <div>
                    <div>
                      {{ selectedCar.item_mobile_make }} {{ selectedCar.item_shared_title || selectedCar.item_mobile_model }} {{ selectedCar.item_shared_title === '' ? ' (ohne Titel)' : '' }}
                    </div>
                    <div class="text-sm text-gray-700">
                      {{ selectedCar.item_fin || 'Ohne FIN' }}
                    </div>
                  </div>
                  <button
                    type="button"
                    class="p-1 text-red-700 bg-red-100 hover:bg-red-600 hover:text-white"
                    @click="unselectCar(selectedCar)"
                  >
                    <mia-streamline-icon
                      class="w-4 h-4 fill-current"
                      src="remove"
                    />
                  </button>
                </li>
              </ul>
            </div>

            <mia-form-section-grid>
              <mia-feature-input
                v-model="form.item_planned_dispatch_from"
                label="Auslieferungszeitraum von"
                type="date"
                :errors="getErrors('item_planned_dispatch_from')"
                @input="clearErrors('item_planned_dispatch_from')"
                @change="onDispatchFromInputChange"
              />

              <mia-feature-input
                v-model="form.item_planned_dispatch_to"
                label="Auslieferungszeitraum bis"
                type="date"
                :errors="getErrors('item_planned_dispatch_to')"
                @input="clearErrors('item_planned_dispatch_to')"
                @change="onDispatchToInputChange"
              />
            </mia-form-section-grid>
          </div>

          <mia-modal-footer class="flex items-center justify-end">
            <mia-cancel-button
              label="Abbrechen"
              type="button"
              :disabled="$wait.any"
              @click="showPlanDispatchableCarsModal = false"
            />

            <mia-submit-button
              label="Fahrzeuge einplanen"
              type="button"
              class="ml-3"
              :disabled="$wait.any"
              @click="onSubmit"
            />
          </mia-modal-footer>
        </mia-form>
      </mia-modal>
    </mia-is-allowed>
  </div>
</template>

<script>
import { getDispatchableCars, planCarDispatches } from '@/api/cars'
import CheckAllColumn from '@/components/ui/datatable/columns/CheckAllColumn.vue'
import CheckboxColumn from '@/components/ui/datatable/columns/CheckboxColumn.vue'
import LinkColumn from '@/components/ui/datatable/columns/LinkColumn.vue'
import { handleEmptyDate, isEmpty, tryFocus } from '@/helpers/utils'
import DateTimeColumn from '@/components/ui/datatable/columns/DateTimeColumn.vue'
import LabelColumn from '@/components/ui/datatable/columns/LabelColumn.vue'
import FinCommColumn from '@/components/ui/datatable/columns/FinCommColumn.vue'
import { DateTime } from 'luxon'
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import Checkbox from '@/components/ui/inputs/Checkbox.vue'
import Spinner from '@/components/ui/loader/Spinner.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import Form from '@/components/ui/widgets/Form.vue'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'
import FlagsColumn from '@/components/cars/search/datatable/FlagsColumn.vue'
import ColumnFilter from '@/components/ui/datatable/ColumnFilter.vue'

export default {
  components: {
    'mia-cancel-button': CancelButton,
    'mia-checkbox': Checkbox,
    'mia-column-filter': ColumnFilter,
    'mia-feature-input': FeatureInput,
    'mia-form': Form,
    'mia-form-section-grid': FormSectionGrid,
    'mia-spinner': Spinner,
    'mia-submit-button': SubmitButton
  },

  mixins: [HandlesBackendValidationErrors],

  data() {
    return {
      dispatchableCars: [],
      filterQuery: '',
      perPage: 25,
      page: 1,
      total: 0,
      visibleColumns: [],
      showPlanDispatchableCarsModal: false,
      includePlannedCars: false,
      form: {
        item_planned_dispatch_from: '',
        item_planned_dispatch_to: ''
      },
      prependedColumns: [
        {
          field: '',
          headerComponent: CheckAllColumn,
          component: CheckboxColumn,
          align: 'center'
        }
      ],
      appendedColumns: [],
      columnConfig: [
        {
          column: 'commissionNumber',
          label: 'FIN / Komm.',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'mobileMake',
          label: 'Marke',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'mobileModel',
          label: 'Modell',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'sharedTitle',
          label: 'Titel',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'flags',
          label: 'Flags',
          isToggleable: true,
          isDefault: false
        },
        {
          column: 'seller',
          label: 'Verkäufer',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'expectedDispatch',
          label: 'Unverbindliches Lieferdatum',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'customer',
          label: 'Kunde',
          isToggleable: true,
          isDefault: false
        },
        {
          column: 'customerZip',
          label: 'Kunde PLZ',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'dispatch',
          label: 'Terminierte Auslieferung',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'status',
          label: 'Fahrzeug-/Produktionsstatus',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'sellCategory',
          label: 'Verkaufskategorie',
          isToggleable: true,
          isDefault: false
        },
        {
          column: 'soldChannel',
          label: 'Vertriebskanal',
          isToggleable: true,
          isDefault: false
        },
        {
          column: 'planned',
          label: 'Eingeplant',
          isToggleable: true,
          isDefault: true
        }
      ],
      otherColumns: {
        commissionNumber: {
          label: 'FIN / Komm.',
          field: 'item_commission_number',
          headerAlign: 'left',
          component: FinCommColumn,
          matches: (row, filterString) => {
            const normalizedFilterString = filterString.trim().toLowerCase()

            const matchesFin = (row.item_fin || '').toLowerCase().includes(normalizedFilterString)
            const matchesCommissionNumber = (row.item_commission_number || '').toLowerCase().includes(normalizedFilterString)

            return matchesFin || matchesCommissionNumber
          },
          meta: {
            schema: '/cars/:id',
            finLabelProp: 'item_fin',
            commLabelProp: 'item_commission_number'
          }
        },
        mobileMake: {
          label: 'Marke',
          field: 'item_mobile_make',
          headerAlign: 'left'
        },
        mobileModel: {
          label: 'Modell',
          field: 'item_mobile_model',
          headerAlign: 'left'
        },
        sharedTitle: {
          label: 'Titel',
          field: 'item_shared_title',
          headerAlign: 'left'
        },
        flags: {
          label: 'Flags',
          field: 'flags',
          headerAlign: 'left',
          component: FlagsColumn
        },
        seller: {
          label: 'Verkäufer',
          field: 'item_seller_id',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: row => (row.item_seller_id ? '/user/profile/:item_seller_id' : ''),
            labelProp: row => (row.item_seller_id ? `${row.item_seller_lastname}, ${row.item_seller_firstname}` : '')
          }
        },
        expectedDispatch: {
          label: 'Unverbindliches Lieferdatum',
          field: 'item_expected_dispatch',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        customer: {
          label: 'Kunde',
          field: 'item_customer_id',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: row => (row.item_customer_id ? '/legacy/customer_edit/:item_customer_id' : ''),
            labelProp: row => [
              row.item_customer_salutation || '',
              row.item_customer_title || '',
              row.item_customer_company || '',
              row.item_customer_firstname || '',
              row.item_customer_name || ''
            ].filter(part => part.trim() !== '').join(' ')
          }
        },
        customerZip: {
          label: 'Kunde PLZ',
          field: 'item_customer_zip',
          headerAlign: 'left'
        },
        dispatch: {
          label: 'Terminierte Auslieferung',
          field: 'item_dispatch',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        status: {
          label: 'Fahrzeug-/Produktionsstatus',
          field: 'item_status',
          headerAlign: 'left'
        },
        sellCategory: {
          label: 'Verkaufskategorie',
          field: 'item_sell_category',
          headerAlign: 'left'
        },
        soldChannel: {
          label: 'Vertriebskanal',
          field: 'item_sold_channel_title',
          headerAlign: 'left'
        },
        planned: {
          label: 'Eingeplant',
          field: 'item_planned',
          headerAlign: 'left',
          component: LabelColumn,
          meta: {
            labelProp: (row) => {
              if (parseInt(row.item_planned, 10) === 1) {
                const parts = []

                if (!isEmpty(handleEmptyDate(row.item_planned_dispatch_from))) {
                  parts.push(DateTime.fromSQL(row.item_planned_dispatch_from).toFormat('dd.MM.yyyy'))
                  parts.push('-')
                }

                if (!isEmpty(handleEmptyDate(row.item_planned_dispatch_to))) {
                  parts.push(DateTime.fromSQL(row.item_planned_dispatch_to).toFormat('dd.MM.yyyy'))
                }

                return parts.join(' ').trim().replace(/-$/, '')
              }

              return 'Nein'
            },
            labelClasses: row => (parseInt(row.item_planned, 10) === 1 ? '' : 'bg-gray-300 border border-gray-400')
          }
        }
      },
      renderDataTable: false
    }
  },

  computed: {
    columns() {
      return [
        ...this.prependedColumns,
        ...this.visibleColumns.map(columnName => this.otherColumns[columnName] || null),
        ...this.appendedColumns
      ].filter(column => column !== null)
    },

    selectedCars() {
      return this.dispatchableCars.filter(car => car.isChecked)
    },

    hasSelectedCars() {
      return this.selectedCars.length > 0
    },

    nextMonth() {
      const nextMonth = DateTime.local().get('month') + 1

      return nextMonth > 12 ? 1 : nextMonth
    },

    month() {
      return DateTime.local().get('month')
    },

    year() {
      return DateTime.local().get('year')
    },

    firstDateOfMonth() {
      return DateTime.local().set({ day: 1 })
    },

    lastDateOfMonth() {
      const year = this.nextMonth < this.month ? this.year + 1 : this.year

      return DateTime.local().set({ day: 1, month: this.nextMonth, year }).minus({ days: 1 })
    },

    hasDispatchableCars() {
      return this.dispatchableCars.length > 0
    }
  },

  watch: {
    includePlannedCars() {
      this.$nextTick(() => {
        this.loadDispatchableCars()
      })
    },

    visibleColumns: {
      deep: true,
      handler(visibleColumns) {
        localStorage.setItem('mia_dispatch_planner_visible_columns', JSON.stringify(visibleColumns))
      }
    }
  },

  created() {
    this.initColumns()
    this.initBreadcrumb()
    this.initForm()
  },

  async mounted() {
    eventBus.$emit('mia-loading-ended')
    await this.loadDispatchableCars()
  },

  methods: {
    initColumns() {
      let userConfiguredColumns = localStorage.getItem('mia_dispatch_planner_visible_columns')

      if (!userConfiguredColumns) {
        userConfiguredColumns = []
      }

      try {
        userConfiguredColumns = JSON.parse(userConfiguredColumns)
      } catch (e) {
        // left blank intentionally
      }

      if (!Array.isArray(userConfiguredColumns)) {
        userConfiguredColumns = []
      }

      if (userConfiguredColumns.length === 0) {
        this.resetVisibleColumns()
        return
      }

      this.visibleColumns = userConfiguredColumns
    },

    initBreadcrumb() {
      eventBus.$emit('mia-set-breadcrumb', [
        {
          link: {
            path: '/dashboard/dispatch-planner'
          },
          text: 'Auslieferungen einplanen'
        }
      ])
    },

    initForm() {
      this.$set(this.form, 'item_planned_dispatch_from', this.firstDateOfMonth.toFormat('yyyy-MM-dd'))
      this.$set(this.form, 'item_planned_dispatch_to', this.lastDateOfMonth.toFormat('yyyy-MM-dd'))
    },

    async loadDispatchableCars() {
      this.$wait.start('loading dispatchable cars')
      this.renderDataTable = false

      const params = {}

      if (this.includePlannedCars) {
        params.include_planned_cars = 1
      }

      const [error, dispatchableCars] = await getDispatchableCars(params)

      this.$wait.end('loading dispatchable cars')

      if (error) {
        console.error(error)
        window.eventBus.$emit('mia-error', {
          error: ['Fahrzeuge konnten nicht geladen werden.']
        })
        return
      }

      this.dispatchableCars = dispatchableCars.map(car => ({
        ...car,
        isChecked: false
      }))

      this.$nextTick(() => {
        this.renderDataTable = true
      })
    },

    planSelectedCars() {
      this.showPlanDispatchableCarsModal = true
    },

    deselectAllCars() {
      this.dispatchableCars = this.dispatchableCars.map(car => ({
        ...car,
        isSelected: false
      }))
    },

    resetVisibleColumns() {
      this.visibleColumns = this.columnConfig.filter(column => column.isDefault).map(({ column }) => column)
    },

    onPageChanged(page) {
      this.page = page
    },

    onPerPageChanged(perPage) {
      this.perPage = parseInt(perPage, 10)
    },

    onPageCountChanged() {
      this.total = this.$refs.datatable.totalRows
    },

    onChecked(cars) {
      this.dispatchableCars = this.dispatchableCars.map(car => ({
        ...car,
        isChecked: cars.find(c => c.id === car.id) !== undefined ? true : car.isChecked
      }))
    },

    onUnchecked(cars) {
      this.dispatchableCars = this.dispatchableCars.map(car => ({
        ...car,
        isChecked: cars.find(c => c.id === car.id) ? false : car.isChecked
      }))
    },

    async onSubmit() {
      const payload = {
        ...this.form,
        cars: this.selectedCars.map(car => car.id)
      }

      this.$wait.start('planning car dispatches')
      this.clearErrors()
      const [error] = await planCarDispatches(payload)
      this.$wait.end('planning car dispatches')

      if (error) {
        this.handleBackendValidationErrors(error)
        console.error(error)
        window.eventBus.$emit('mia-error', {
          error: ['Daten konnten nicht gespeichert werden.']
        })
        return
      }

      if (this.includePlannedCars) {
        this.dispatchableCars = this.dispatchableCars.map(car => ({
          ...car,
          item_planned: this.selectedCars.find(c => c.id === car.id) !== undefined ? '1' : car.item_planned
        }))
      } else {
        this.dispatchableCars = this.dispatchableCars.filter(car => this.selectedCars.find(c => c.id === car.id) === undefined)
      }

      this.deselectAllCars()
      this.showPlanDispatchableCarsModal = false

      window.eventBus.$emit('mia-info', {
        info: ['Fahrzeuge erfolgreich eingeplant.']
      })
    },

    onPlanDispachableCarsModalOpened() {
      this.$nextTick(() => {
        tryFocus(this.$refs.plannedDispatchContainerRef.querySelector('input[type=date]'))
      })
    },

    unselectCar(car) {
      this.dispatchableCars = this.dispatchableCars.map((c) => {
        if (c.id === car.id) {
          return {
            ...c,
            isChecked: false
          }
        }

        return c
      })

      if (!this.hasSelectedCars) {
        this.showPlanDispatchableCarsModal = false
      }
    },

    onDispatchFromInputChange() {
      const fromDate = DateTime.fromSQL(this.form.item_planned_dispatch_from)
      const toDate = DateTime.fromSQL(this.form.item_planned_dispatch_to)

      if (!fromDate.isValid || !toDate.isValid) {
        return
      }

      if (fromDate > toDate) {
        const nextEndDate = fromDate
          .set({ day: 1 })
          .plus({ month: 1 })
          .minus({ day: 1 })
          .toFormat('yyyy-MM-dd')

        this.$set(this.form, 'item_planned_dispatch_to', nextEndDate)
      }
    },

    onDispatchToInputChange() {
      const fromDate = DateTime.fromSQL(this.form.item_planned_dispatch_from)
      const toDate = DateTime.fromSQL(this.form.item_planned_dispatch_to)

      if (!fromDate.isValid || !toDate.isValid) {
        return
      }

      if (toDate < fromDate) {
        const previousFromDate = toDate
          .set({ day: 1 })
          .toFormat('yyyy-MM-dd')

        this.$set(this.form, 'item_planned_dispatch_from', previousFromDate)
      }
    }
  }
}
</script>
